/*!
 * calculoAcm.ts — Motor de cálculo das portas premium em ACM (Método ACM)
 * Perfis Day Brasil 2620/2621 (Linha Social) e 2622/2623 (Linha Interna)
 * Revestimento TECBOND ACM para Portas · 3 mm
 * v3.2 · 04/09/2026 · Evolua Negócios · Método ACM
 */

import { chapasNecessarias, PecaAcm } from './acmChapas';

export const REGRAS = {
    U_EXTERNO: 7,
    PASSE_ALTURA: 8,
    ENTRA_EM_VAO: 7,
    NIVELAMENTO: 3,
    BORDA_INFINITA: 20,
    PEGADOR: 46,
    RECUO_BORDA: 4,
    VAO_TRAVESSA_MAX: 600,
    BARRA: 6000,
    KERF: 5,
    /** O batente sai da barra sem descontar o corte: os dois batentes de uma porta de 3 m fecham 1 barra de 6 m. */
    KERF_BATENTE: 0,
    ACM_KG_M2: 4.6,
    FATOR_FERRAGEM: 1.3
};

export const CAVA = {
    vao: 150,
    parede: 19,
    aba: 40,
    face: 140,
    virada: 19,
    folga: 7
};

/**
 * Padrões dos detalhes editáveis. Os valores de cava/junta são os validados pelo Método ACM;
 * a folga de divisão e a face mínima são adotados pelo editor (conferir na primeira porta executada).
 */
export const DETALHES_PADRAO = {
    /** Campo entre o montante da borda e o montante da cava (zona do puxador). */
    CAVA_AFASTAMENTO: 100,
    /** Folga entre duas chapas de uma divisão manual (corte reto, sem borda infinita). */
    FOLGA_DIVISAO: 2,
    /** Menor face de chapa que o editor aceita. */
    FACE_MINIMA: 50
};

/** Medidas de referência das chapas ripada e frisada (folders da linha; ainda não validadas pelo Método ACM). */
/** Profundidade da ripa alta do puxador integrado. */
export const PUXADOR_RIPA_PROF = 60;

export const CHAPA_TIPOS_PADRAO = {
    ripada: { ripa: 45, vao: 15, prof: 20, abas: 30 },
    frisada: { passo: 100, largura: 3, fresaMin: 20 }
};

export type TipoChapa = 'lisa' | 'ripada' | 'frisada';

/** Tipo de uma chapa (face). Ripada: ripas dobradas em ACM; frisada: fresas decorativas na face. */
export interface ChapaTipoEntrada {
    tipo: TipoChapa;
    /** Ripada: largura (face) da ripa e vão entre ripas, em mm. */
    ripa?: number;
    vao?: number;
    /** Ripada: cor da chapa de fundo, onde as ripas são coladas (aparece nos vãos). Sem ela vale a cor das ripas. */
    fundo?: string;
    /** Frisada: distância entre as fresas e direção das linhas. */
    passo?: number;
    direcao?: 'vertical' | 'horizontal';
}

/** Porta de correr: folha na frente da parede, cabeçote (trilho) por cima do vão. Medidas de referência dos folders da linha. */
export const CORRER_DIM = { sobreposicaoLateral: 50, sobreposicaoTopo: 40, folgaPiso: 10, cabecoteAltura: 130, cabecoteProf: 110, bandejaMax: 1150 };

export const MODELOS: any = {
    social: {
        id: 'social', linha: 'LINHA SOCIAL', nome: 'Porta pivotante Social',
        titulo: 'PORTA PIVOTANTE SOCIAL EM ACM',
        temCava: true, aceitaCava: true, ferragem: 'pivo',
        perfilFolha:   { cod: '2621', nome: 'Perfil Social Folha',   secao: '80×40 #1,4', kgm: 0.790 },
        perfilBatente: { cod: '2620', nome: 'Perfil Social Batente', secao: '80×40 #1,4', kgm: 1.015 },
        d: 80,
        descontoLargura: 118, descontoAltura: 71,
        alturaMax: 6000, alturaMin: 2100,
        vergaDesconto: 80,
        // Desenho do conjunto: largura/altura do batente à vista e folga entre a folha e o piso
        btL: 43, btT: 43, folgaInf: 12,
        tipoMarco: 'batente', larguraMaxChapa: 1500,
        uBorda: { desenv: 167, nome: '60×47×60' },
        junta: { faixa: 250, vao: 200, friso: 20, referencia: 'pivô' },
        validado: true
    },
    interna: {
        id: 'interna', linha: 'LINHA INTERNA', nome: 'Porta interna de giro',
        titulo: 'PORTA INTERNA DE GIRO EM ACM',
        temCava: false, aceitaCava: true, ferragem: 'dobradica',
        perfilFolha:   { cod: '2622', nome: 'Perfil Interno Folha',   secao: '25×52 #1,3', kgm: 0.502 },
        perfilBatente: { cod: '2623', nome: 'Perfil Interno Batente', secao: '19×52 #1,3', kgm: 0.657 },
        d: 52,
        descontoLargura: 74, descontoAltura: 54,
        alturaMax: 3000, alturaMin: 2100,
        vergaDesconto: 38,
        btL: 22, btT: 28, folgaInf: 20,
        tipoMarco: 'batente', larguraMaxChapa: 1500,
        uBorda: { desenv: 112, nome: '40×32×40' },
        junta: { faixa: 150, vao: 140, friso: 20, referencia: 'fechadura' },
        validado: false
    },
    correr: {
        id: 'correr', linha: 'LINHA DE CORRER', nome: 'Porta de correr',
        titulo: 'PORTA DE CORRER EM ACM',
        temCava: false, ferragem: 'roldana',
        perfilFolha:   { cod: '2622', nome: 'Perfil de correr — folha',   secao: '25×50', kgm: 0.502 },
        perfilBatente: { cod: '2623', nome: 'Perfil de correr — marco',   secao: '19×52', kgm: 0.657 },
        d: 40,
        // A folha é maior que o vão: sobrepõe a parede 50 de cada lado e 40 no topo, com 10 de folga do piso
        descontoLargura: -2 * CORRER_DIM.sobreposicaoLateral,
        descontoAltura: -(CORRER_DIM.sobreposicaoTopo - CORRER_DIM.folgaPiso),
        alturaMax: 3000, alturaMin: 1800,
        vergaDesconto: 0,
        btL: 0, btT: 0, folgaInf: CORRER_DIM.folgaPiso,
        tipoMarco: 'cabecote', larguraMaxChapa: CORRER_DIM.bandejaMax + 2 * 20 /* BI nas duas bordas */,
        divisaoAutomatica: true,
        uBorda: { desenv: 112, nome: '40×32×40' },
        junta: { faixa: 150, vao: 140, friso: 20, referencia: 'fechadura' },
        validado: false
    }
};

// Tons conferidos na cartela de cores ACM da Day Brasil (amostrados da cartela em 23/09/2026).
// Equivalências: Branco = Branco Neve · Champanhe = Champagne Metálico · Cinza Chumbo = Grafite Chumbo
// · Preto = Preto Carbono · Carvalho Dourado = Madeira Mogno. Esses nomes ficam como estão (já gravados
// nas portas e nas regras de preço). As demais cores da cartela (fotos de 23/10/2026) entram com o nome da Day;
// Madeira Carvalho, Madeira Avelã, Concreto, Marrom e Corten foram atualizados com o tom da foto.
export const PALETA = [
    ['Madeiras',        { 'Madeira Carvalho': '#8e764a', 'Madeira Avelã': '#644b2a', 'Carvalho Dourado': '#754520', 'Madeira Carvalho Dourado': '#8c4a14', 'Madeira Cedro': '#7d3107', 'Madeira Mogno': '#7c4a11', 'Madeira Sucupira': '#312115', 'Madeira Vintage': '#663310' }],
    ['Neutros claros',  { 'Branco': '#fdfdfd', 'Branco Neve Brilho': '#fbfcfa', 'Concreto': '#897f74', 'Champanhe': '#8b8377', 'Cinza Acetinado': '#d5d9e1', 'Cinza Acetinado Geely': '#bebcb9', 'Prata Metálico': '#dbd6d0' }],
    ['Neutros escuros', { 'Cinza Escuro': '#3b3d3a', 'Dark Grey': '#393c36', 'Cinza Grey': '#4d504c', 'Cinza Steel': '#686a65', 'Cinza RAL 7037': '#807c75', 'Cinza Chumbo': '#5a5a59', 'Preto': '#020304', 'Preto Carbono Brilho': '#28282d' }],
    ['Metálicos',       { 'Cinza Metálico': '#ccd0d7', 'Grafite Metálico': '#454545' }],
    ['Azuis',           { 'Azul Royal': '#005bb5', 'Azul Marinho': '#10286b', 'Azul Marinho Brilho': '#3d28cd', 'Azul Noturno': '#1a2a41' }],
    ['Verdes',          { 'Verde BR': '#237348', 'Verde Escuro': '#00485c', 'Verde Finlandia': '#00757d', 'Verde Folha': '#13c422', 'Verde Fosco Localiza': '#006a29' }],
    ['Amarelos e laranjas', { 'Amarelo Real': '#f5c70a', 'Amarelo Real Brilho': '#ffc000', 'Laranja Silvestre': '#d66425', 'Laranja Silvestre Brilho': '#ea7736' }],
    ['Vermelhos',       { 'Vermelho Intenso': '#ba211b', 'Vermelho Intenso Brilho': '#f50000', 'Vermelho Karmin': '#930001', 'Vinho Cintilante': '#8f1424' }],
    ['Terrosos',        { 'Marrom': '#4a1d00', 'Corten': '#66422d', 'Bronze 1003': '#4b3c24' }]
];

/**
 * Acabamento visual de cada cor (usado na imagem 3D da proposta e no visualizador).
 * `textura` é a foto da chapa em /texturas-acm (madeiras, Concreto, Corten); `veios` e o tom da paleta
 * servem de reserva enquanto a foto carrega. `brilho` marca as chapas de acabamento brilhante.
 */
export interface AcabamentoCor {
    tipo: 'solido' | 'metalico' | 'madeira';
    veios?: [string, string, string];
    textura?: string;
    brilho?: boolean;
}

export const ACABAMENTO_COR: Record<string, AcabamentoCor> = {
    'Madeira Carvalho': { tipo: 'madeira', veios: ['#7e653c', '#8f774a', '#9d8758'], textura: 'madeira-carvalho' },
    'Madeira Avelã': { tipo: 'madeira', veios: ['#523b1f', '#624a29', '#775c36'], textura: 'madeira-avela' },
    'Carvalho Dourado': { tipo: 'madeira', veios: ['#6d3d0a', '#7c4a10', '#8c5817'], textura: 'madeira-mogno' }, // Madeira Mogno Day
    'Madeira Carvalho Dourado': { tipo: 'madeira', veios: ['#753406', '#8d4911', '#a66124'], textura: 'madeira-carvalho-dourado' },
    'Madeira Cedro': { tipo: 'madeira', veios: ['#6a2403', '#7f3004', '#8e3f10'], textura: 'madeira-cedro' },
    'Madeira Mogno': { tipo: 'madeira', veios: ['#6d3d0a', '#7c4a10', '#8c5817'], textura: 'madeira-mogno' },
    'Madeira Sucupira': { tipo: 'madeira', veios: ['#2a1b10', '#312116', '#37271b'], textura: 'madeira-sucupira' },
    'Madeira Vintage': { tipo: 'madeira', veios: ['#481d04', '#66320e', '#84491d'], textura: 'madeira-vintage' },
    'Concreto': { tipo: 'solido', textura: 'concreto' },
    'Corten': { tipo: 'solido', textura: 'corten' },
    'Champanhe': { tipo: 'metalico' },
    'Cinza Acetinado': { tipo: 'metalico' },
    'Cinza Acetinado Geely': { tipo: 'metalico' },
    'Cinza Metálico': { tipo: 'metalico' },
    'Grafite Metálico': { tipo: 'metalico' },
    'Prata Metálico': { tipo: 'metalico' },
    'Vinho Cintilante': { tipo: 'metalico' },
    'Branco Neve Brilho': { tipo: 'solido', brilho: true },
    'Preto Carbono Brilho': { tipo: 'solido', brilho: true },
    'Azul Marinho Brilho': { tipo: 'solido', brilho: true },
    'Amarelo Real Brilho': { tipo: 'solido', brilho: true },
    'Laranja Silvestre Brilho': { tipo: 'solido', brilho: true },
    'Vermelho Intenso Brilho': { tipo: 'solido', brilho: true },
};

export const CORES: Record<string, string> = {};
PALETA.forEach((g: any) => { Object.keys(g[1]).forEach(k => { CORES[k] = g[1][k]; }); });

export const CORES_PADRAO: Record<string, string> = {
    A1: 'Carvalho Dourado', A2: 'Champanhe', A3: 'Corten',
    B1: 'Preto', B2: 'Corten', B3: 'Cinza Chumbo',
    cavaA: 'Champanhe', cavaB: 'Preto', fundo: 'Preto', U: 'Carvalho Dourado'
};

/** Slot do lado B -> slot equivalente do lado A, usado quando o lado B não é informado. */
const ESPELHO_LADO_B: Record<string, string> = { B1: 'A1', B2: 'A2', B3: 'A3', cavaB: 'cavaA' };

export function nomeComercial(cor: string) { return 'TECBOND ' + cor; }

/**
 * Plano de corte das barras de 6 m de um perfil. `kerf` é a perda de cada corte (o batente usa KERF_BATENTE).
 * Peça acima de 6 m leva emenda: cada 6 m inteiros ocupa uma barra, e a ponta que sobra entra no encaixe como
 * uma peça comum (as pontas de vários lados saem da mesma barra).
 */
export function planoBarras(cortes: any[], codPerfil: string, kerf: number = REGRAS.KERF) {
    let pecas: any[] = [];
    let barras: any[] = [];
    cortes.forEach(c => {
        if (c.perfil !== codPerfil) return;
        for (let i = 0; i < c.qtd; i++) {
            if (c.comprimento <= REGRAS.BARRA) { pecas.push({ pos: c.pos, comprimento: c.comprimento }); continue; }
            let resto = c.comprimento;
            for (; resto > REGRAS.BARRA; resto -= REGRAS.BARRA) barras.push({ itens: [{ pos: c.pos, comprimento: REGRAS.BARRA, emenda: true }], uso: REGRAS.BARRA });
            if (resto > 0) pecas.push({ pos: c.pos, comprimento: resto, emenda: true });
        }
    });
    pecas.sort((a, b) => b.comprimento - a.comprimento);
    pecas.forEach(p => {
        let b = null;
        for (let i = 0; i < barras.length; i++) {
            if (barras[i].uso + p.comprimento + kerf <= REGRAS.BARRA) { b = barras[i]; break; }
        }
        if (!b) { b = { itens: [], uso: 0 }; barras.push(b); }
        b.itens.push(p); b.uso += p.comprimento + kerf;
    });
    return barras.map((b, i) => {
        return { barra: i + 1, itens: b.itens, usado: b.uso, sobra: REGRAS.BARRA - b.uso };
    });
}

export function calcularPeca02(folhaAltura: number, vaoCava: number = CAVA.vao) {
    let fundo = vaoCava - CAVA.folga;
    let extremidade = CAVA.virada + CAVA.face;
    let largura = 2 * CAVA.aba + 2 * CAVA.parede + fundo;
    let fundoComprimento = folhaAltura - 2 * CAVA.face;
    let comprimento = 2 * extremidade + 2 * CAVA.parede + fundoComprimento;
    return {
        chapa: { largura: largura, comprimento: comprimento },
        fresasLargura: [CAVA.aba, CAVA.parede, fundo, CAVA.parede, CAVA.aba],
        fresasComprimento: [extremidade, CAVA.parede, fundoComprimento, CAVA.parede, extremidade],
        vao: vaoCava,
        fundo: fundo, parede: CAVA.parede, aba: CAVA.aba,
        extremidade: extremidade, virada: CAVA.virada, face: CAVA.face,
        folgaPorLado: (vaoCava - fundo) / 2,
        fresaLarguraInicio: extremidade,
        fresaLarguraFim: comprimento - extremidade,
        abaSemFresa: extremidade,
        conferenciaAltura: CAVA.face + fundoComprimento + CAVA.face
    };
}

export interface CalcularPortaParams {
    modelo: 'social' | 'interna' | 'correr';
    vaoLargura: number;
    vaoAltura: number;
    fechaduraEixo?: number;
    cava?: boolean;
    junta?: boolean;
    juntaFaixa?: number;
    juntaVao?: number;
    juntaFriso?: number;
    cores?: Record<string, string>;
    projeto?: string;
    /** Formato antigo (links 3D já gerados): 'ripada' e 'correr' agora são preset da social e modelo `correr`. */
    tipo?: 'ripada' | 'correr';
    /** Pivotante ripada: porta social com chapas ripadas e puxador integrado numa ripa alta. */
    linhaRipada?: boolean;
    /** Ripada: liga/desliga o puxador integrado (ripa alta). Padrão: ligado na linha ripada. */
    puxadorRipa?: boolean;
    /** Limite de altura do vão desta porta (sobre o da linha). Ex.: ripada até 5.000. */
    alturaMaxima?: number;
    /** Conjunto: medidas da folha dadas direto (em vez de vão − desconto), para folhas de uma dupla, fixos e bandeiras. */
    folhaLargura?: number;
    folhaAltura?: number;
    /** Conjunto: o marco é calculado no conjunto, então esta folha não traz batente nem verga. */
    semMarco?: boolean;
    /** 'fixa': painel fixo (sem cava, ferragem, fechadura nem U de borda). Padrão: folha de giro/correr. */
    papel?: 'giro' | 'fixa';
    /** Conjunto: variação no vão — 'simples' (padrão), 'dupla' (2 folhas), 'bandeira' (painel fixo acima) ou 'fixo-lateral'. */
    variacao?: 'simples' | 'dupla' | 'bandeira' | 'fixo-lateral';
    /** Bandeira: altura da região acima da folha (inclui a travessa do marco), em mm. */
    bandeiraAltura?: number;
    /** Fixo lateral: largura da região do painel fixo (sem a trave do marco) e o lado em que fica. */
    fixoLargura?: number;
    fixoLado?: 'esq' | 'dir';
    /** Folha espelhada (puxador do outro lado). Na dupla a folha 1 é sempre espelhada. */
    espelhar?: boolean;
    /** Detalhes (cava, frisos, perfis, chapas, cores) das folhas além da principal. Chave: 'f2' (dupla) ou 'fixo'. */
    folhas?: Record<string, DetalhesFolha>;
    /** Cor do kit (maçaneta, dobradiças, puxador sobreposto, concha): 'Preto' ou 'Escovado'. */
    corKit?: string;
    ladoAbertura?: string;
    /** Inclui o alisar (moldura plana por fora do marco, encostada na parede) no 3D e na imagem da proposta. */
    alisar?: boolean;
    /** Largura do alisar em mm (20 a 70). */
    alisarMm?: number;
    /** Editor: campo entre o montante da borda e o montante da cava, em mm (padrão 100). Define onde a cava fica. */
    cavaAfastamento?: number;
    /** Editor: vão da cava na estrutura, em mm (padrão 150). */
    cavaVao?: number;
    /** Editor: posições x (mm, a partir da borda do puxador/fechadura da folha) de divisões manuais de chapa. */
    divisoes?: number[];
    /** Frisos automáticos quando uma chapa passa da chapa comercial (padrão: ligado). `false` = só avisar. */
    frisoAuto?: boolean;
    /** Largura da chapa de ACM (1220, 1250 ou 1500). Sem ela, o motor escolhe a melhor para a porta. */
    larguraChapa?: number;
    /** Espessura da chapa de ACM escolhida no orçamento (3 ou 4 mm); sem ela vale a da altura do vão. */
    espessuraChapa?: number;
    /**
     * Editor: lista de frisos (juntas rebaixadas), verticais ou horizontais, em qualquer posição.
     * Quando existe (mesmo vazia) substitui `junta` / `juntaFaixa` / `juntaVao` / `juntaFriso`.
     */
    frisos?: FrisoEntrada[];
    /** Editor: cor própria da cava em cada lado (vale sobre `cores.cavaA` / `cores.cavaB`). */
    cavaCorA?: string;
    cavaCorB?: string;
    /**
     * Editor: alturas (do piso da folha) do centro de cada travessa intermediária. Quando existe (mesmo vazia)
     * substitui as travessas automáticas (que mantêm cada vão ≤ 600); as dos frisos horizontais continuam.
     */
    travessas?: number[];
    /** Editor: x do centro de montantes extras (perfis verticais a mais), a partir da borda do puxador/fechadura. */
    montantesExtras?: number[];
    /** Editor: tipo de cada chapa. Chave = lado + rótulo ('A2', 'B1.2'). Sem entrada vale `chapaPadrao`, ou lisa. */
    chapaTipos?: Record<string, ChapaTipoEntrada>;
    /** Editor: tipo de todas as chapas que não têm entrada própria em `chapaTipos`. */
    chapaPadrao?: ChapaTipoEntrada;
}

/** Campos que descrevem uma folha em si (o resto — vão, linha, variação — é do conjunto). */
export const CAMPOS_DA_FOLHA = [
    'cava', 'cavaAfastamento', 'cavaVao', 'cavaCorA', 'cavaCorB', 'divisoes', 'frisos', 'junta', 'juntaFaixa', 'juntaVao', 'juntaFriso',
    'travessas', 'montantesExtras', 'chapaTipos', 'chapaPadrao', 'puxadorRipa',
] as const;

export type DetalhesFolha = Partial<Pick<CalcularPortaParams, typeof CAMPOS_DA_FOLHA[number]>> & { cores?: Record<string, string> };

export type OrientacaoFriso = 'vertical' | 'horizontal';

export interface FrisoEntrada {
    orientacao?: OrientacaoFriso;
    /** Vertical: x do centro a partir da borda do puxador/fechadura. Horizontal: altura do centro a partir da base da folha. */
    posicao: number;
    /** Vão livre da junta (padrão da linha: Social 200, Interna 140). */
    vao?: number;
    /** Friso aparente (padrão 20). */
    friso?: number;
    /** Cor do fundo deste friso; sem ela vale `cores.fundo` (ou `cores.fundoN`). */
    cor?: string;
    /** Friso inserido pelo motor para unir chapas que passariam da chapa comercial. Editar o friso o torna manual. */
    auto?: boolean;
}

/**
 * Peças do marco (batentes, verga e revestimentos em ACM; ou o cabeçote da correr) para um vão VL × VA.
 * Separado do cálculo da folha para que o conjunto (bandeira, fixo lateral, dupla) calcule o marco uma vez só.
 */
export function marcoDaPorta(m: any, VL: number, VA: number, FL: number, corU: string) {
    const cortes: any[] = [];
    const acm: any[] = [];
    const cF = m.perfilFolha.cod, cB = m.perfilBatente.cod;
    // Correr: o marco é o cabeçote (trilho + roldanas, kit), sem verga nem batente em perfil
    if (m.tipoMarco !== 'cabecote') cortes.push(
        { pos: 'V1', peca: 'Verga do vão (tubo)', perfil: cF, qtd: 1, comprimento: VL - m.vergaDesconto, grupo: 'marco' },
        { pos: 'B1', peca: 'Batente vertical do vão', perfil: cB, qtd: 2, comprimento: VA, grupo: 'marco' }
    );
    const addMarco = (pos: string, peca: string, qtd: number, larg: number, comp: number, obs: string) => {
        acm.push({ pos, peca, slot: 'U', cor: corU, corComercial: nomeComercial(corU), qtd, chapaLargura: larg, chapaComprimento: comp, obs, grupo: 'marco' });
    };
    const comprimentoCabecote = VL + FL + 2 * 40; // cobre a folha fechada e a posição aberta
    if (m.tipoMarco === 'cabecote') {
        addMarco('CB', 'Revestimento do cabeçote (trilho)', 1, CORRER_DIM.cabecoteAltura * 2 + CORRER_DIM.cabecoteProf + 40, comprimentoCabecote, 'cobre o trilho no vão e na posição aberta · 3 faces');
    } else if (m.temCava) {
        addMarco('BT1', 'Cantoneira de Acabamento 44×81', 2, 145, VA, 'BI: 1 lado (aba de 44)');
        addMarco('BT2', 'Barra Chata 65', 2, 105, VA, 'BI: 2 lados');
        addMarco('BT3', 'U Superior 43×87×43 (verga)', 1, 173, VL - 80, 'sem BI · ajuste na montagem');
        addMarco('V2', 'Peça de 20 — espaçador da verga', 1, 20, VL - 80, 'colada e parafusada');
        addMarco('V3', 'Aba de 100 — verga (fresta do alizar)', 1, 100, VL - 80, 'transpasse de 10 por lado');
    } else {
        addMarco('BT1', 'Cantoneira de Acabamento 22×53 — alturas', 2, 95, VA, 'BI: 1 lado (aba de 22)');
        addMarco('BT2', 'Barra Chata 38 — alturas', 2, 78, VA, 'BI: 2 lados');
        addMarco('BT3', 'Cantoneira de Acabamento 22×53 — verga', 1, 95, VL - m.vergaDesconto, 'BI: 1 lado');
        addMarco('BT4', 'Barra Chata 38 — verga', 1, 78, VL - m.vergaDesconto, 'BI: 2 lados');
    }
    return { cortes, acm, comprimentoCabecote };
}

/** Converte o formato antigo (`tipo: 'ripada' | 'correr'`) no atual; devolve os parâmetros como vieram nos demais casos. */
export function normalizarParams(e: CalcularPortaParams): CalcularPortaParams {
    if (!e?.tipo) return e;
    const { tipo, ...resto } = e;
    if (tipo === 'correr') return { ...resto, modelo: 'correr', cava: false };
    return { ...resto, modelo: 'social', cava: false, linhaRipada: true, alturaMaxima: e.alturaMaxima ?? RIPADA_ALTURA_MAX };
}

/** Altura máxima do vão da pivotante ripada (referência dos folders). */
export const RIPADA_ALTURA_MAX = 5000;

function calcularPortaBase(entrada: CalcularPortaParams) {
    const e = normalizarParams(entrada);
    if (!e || !MODELOS[e.modelo]) throw new Error('modelo inválido: use "social", "interna" ou "correr"');
    let m = e.alturaMaxima ? { ...MODELOS[e.modelo], alturaMax: e.alturaMaxima } : MODELOS[e.modelo];
    let VL = Number(e.vaoLargura) || 0;
    let VA = Number(e.vaoAltura) || 0;
    let FE = e.fechaduraEixo == null ? 1000 : Number(e.fechaduraEixo);
    let d = m.d;
    let FL = e.folhaLargura != null ? Number(e.folhaLargura) : VL - m.descontoLargura;
    let FA = e.folhaAltura != null ? Number(e.folhaAltura) : VA - m.descontoAltura;
    const fixa = e.papel === 'fixa';

    let cavaOn = !!((m.aceitaCava ?? m.temCava) && e.cava && !fixa);
    const num = (v: any, def: number) => (v == null || v === '' || !Number.isFinite(Number(v)) ? def : Number(v));
    let cAfast = Math.max(0, num(e.cavaAfastamento, DETALHES_PADRAO.CAVA_AFASTAMENTO));
    let cVao = Math.max(CAVA.aba + 10, num(e.cavaVao, CAVA.vao));
    let divisoesIn: number[] = (Array.isArray(e.divisoes) ? e.divisoes : [])
        .map(Number).filter(Number.isFinite).sort((a, b) => a - b);

    // ---- Frisos: lista do editor ou a junta única de sempre (um friso vertical perto do pivô/da fechadura)
    const centroLegado = (faixa: number, vao: number) => m.id === 'social'
        ? FL - d - (faixa + (vao - d) / 2) - d / 2
        : d + (faixa + (vao - d) / 2) + d / 2;
    const faixaDoCentro = (c: number, vao: number) => m.id === 'social'
        ? FL - d - (c + d / 2) - (vao - d) / 2
        : (c - d / 2) - d - (vao - d) / 2;
    let frisosIn: FrisoEntrada[];
    if (Array.isArray(e.frisos)) frisosIn = e.frisos;
    else if (e.junta) {
        const vao = Number(e.juntaVao) || m.junta.vao;
        frisosIn = [{ orientacao: 'vertical', posicao: centroLegado(num(e.juntaFaixa, m.junta.faixa), vao), vao, friso: Number(e.juntaFriso) || m.junta.friso }];
    } else frisosIn = [];
    const frisoPadraoLegado = { posicao: centroLegado(m.junta.faixa, m.junta.vao), vao: m.junta.vao, friso: m.junta.friso };

    let cores: Record<string, string> = { ...CORES_PADRAO };
    if (e.cores) {
        Object.keys(e.cores).forEach(k => { cores[k] = e.cores![k]; });
        // Porta de um lado só (só o lado A foi informado): o lado B acompanha o A,
        // em vez de cair nas cores da porta base (B1 Preto, B2 Corten...).
        Object.keys(ESPELHO_LADO_B).forEach(b => {
            const a = ESPELHO_LADO_B[b];
            if (!e.cores![b] && e.cores![a]) cores[b] = e.cores![a];
        });
    }
    if (e.cavaCorA) cores.cavaA = e.cavaCorA;
    if (e.cavaCorB) cores.cavaB = e.cavaCorB;
    // Fundos de friso extras (fundo2, fundo3...) seguem a cor do primeiro enquanto não tiverem a própria
    let cor = (slot: string) => cores[slot] || (slot.startsWith('fundo') ? cores.fundo : '') || 'Preto';

    const frisos: any[] = frisosIn.map((f, i) => {
        const horizontal = f.orientacao === 'horizontal';
        const vao = Math.max(0, num(f.vao, m.junta.vao));
        const friso = Math.max(0, num(f.friso, m.junta.friso));
        const posicao = num(f.posicao, 0);
        const slot = i === 0 ? 'fundo' : 'fundo' + (i + 1);
        const corFriso = f.cor || cor(slot);
        return {
            indice: i, slot, orientacao: horizontal ? 'horizontal' : 'vertical',
            posicao, vaoLivre: vao, friso, avanco: (vao - friso) / 2, fundoChapa: 2 * d + vao,
            rebaixo: REGRAS.NIVELAMENTO, cor: corFriso, corExplicita: f.cor || null, auto: !!f.auto,
            // x do centro (vertical) ou y do centro medido do topo da folha (horizontal)
            centro: horizontal ? FA - posicao : posicao
        };
    });
    const verticais = frisos.filter(f => f.orientacao === 'vertical');
    const horizontais = frisos.filter(f => f.orientacao === 'horizontal').sort((a, b) => a.centro - b.centro);

    // ---- Montantes verticais (bordas, cava, frisos verticais) e os campos entre eles
    type Mont = { x0: number; x1: number; tag: string; friso?: number };
    const monts: Mont[] = [{ x0: 0, x1: d, tag: 'borda-esq' }, { x0: FL - d, x1: FL, tag: 'borda-dir' }];
    const xCava0 = 2 * d + cAfast;
    if (cavaOn) {
        monts.push({ x0: xCava0 - d, x1: xCava0, tag: 'cava-esq' }, { x0: xCava0 + cVao, x1: xCava0 + cVao + d, tag: 'cava-dir' });
    }
    verticais.forEach(f => monts.push({ x0: f.centro - d / 2, x1: f.centro + d / 2, tag: 'friso', friso: f.indice }));
    const extrasIn: number[] = (Array.isArray(e.montantesExtras) ? e.montantesExtras : []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    extrasIn.forEach((c, i) => monts.push({ x0: c - d / 2, x1: c + d / 2, tag: 'extra', friso: i }));
    monts.sort((a, b) => a.x0 - b.x0);

    let Z: any[] = [];
    // Montantes que se encostam/sobrepõem, ou um friso dentro da cava, não fecham a estrutura
    let sobrepoe = false;
    monts.forEach((mt, k) => {
        const seguinte = monts[k + 1];
        if (seguinte && (seguinte.x0 < mt.x1 || (mt.tag === 'cava-esq' && seguinte.tag !== 'cava-dir'))) sobrepoe = true;
        const papel = mt.tag === 'borda-esq' || mt.tag === 'borda-dir' ? 'borda' : mt.tag === 'friso' ? 'friso' : mt.tag === 'extra' ? 'extra' : 'cava';
        Z.push({ tipo: 'montante', largura: mt.x1 - mt.x0, x0: mt.x0, x1: mt.x1, papel,
                 ...(mt.tag === 'friso' ? { isJuntaCenter: true, friso: mt.friso } : {}),
                 ...(mt.tag === 'extra' ? { extra: mt.friso } : {}) });
        const prox = monts[k + 1];
        if (!prox) return;
        if (mt.tag === 'cava-esq' && prox.tag === 'cava-dir') {
            Z.push({ tipo: 'cava', largura: prox.x0 - mt.x1, x0: mt.x1, x1: prox.x0 });
            return;
        }
        let nome = 'campo';
        if (prox.tag === 'cava-esq' && mt.tag === 'borda-esq') nome = 'zona do puxador';
        else if (m.id === 'social' && prox.tag === 'borda-dir' && mt.tag === 'friso') nome = 'faixa do pivô';
        else if (m.id !== 'social' && mt.tag === 'borda-esq' && prox.tag === 'friso') nome = 'faixa';
        Z.push({ tipo: 'campo', largura: prox.x0 - mt.x1, x0: mt.x1, x1: prox.x0, nome });
    });

    // A zona da junta é só uma marca sobre o desenho (vão livre centrado no montante); não entra na soma da largura
    verticais.forEach(f => Z.push({ tipo: 'junta', largura: f.vaoLivre, x0: f.centro - f.vaoLivre / 2, x1: f.centro + f.vaoLivre / 2, friso: f.indice }));

    let campos    = Z.filter(z => z.tipo === 'campo');
    let montantes = Z.filter(z => z.tipo === 'montante');
    let zCava     = Z.filter(z => z.tipo === 'cava')[0] || null;

    // ---- Travessas: as das bordas, uma em cada friso horizontal e as que mantêm cada vão ≤ 600
    // (ou, na edição manual, as que o usuário colocou). Cada item guarda se pertence a um friso.
    const fixas: Array<{ top: number; bottom: number; friso?: any }> = [{ top: 0, bottom: d }];
    horizontais.forEach(f => fixas.push({ top: f.centro - d / 2, bottom: f.centro + d / 2, friso: f }));
    fixas.push({ top: FA - d, bottom: FA });
    const vaosFixas: number[] = [];
    for (let k = 0; k < fixas.length - 1; k++) vaosFixas.push(fixas[k + 1].top - fixas[k].bottom);
    const travessaManual = Array.isArray(e.travessas);
    let itensTravessa: Array<{ top: number; centro: number; friso: number | null; manual: boolean }> = [];
    let vaosTravessa: number[] = [];
    if (travessaManual) {
        const manuais = (e.travessas as number[]).map(Number).filter(Number.isFinite).map(h => ({ top: FA - h - d / 2, friso: null as number | null }));
        const frisosT = horizontais.map(f => ({ top: f.centro - d / 2, friso: f.indice as number | null }));
        itensTravessa = [...frisosT, ...manuais].sort((a, b) => a.top - b.top).map(t => ({ top: t.top, centro: t.top + d / 2, friso: t.friso, manual: t.friso == null }));
        let anterior = d;
        itensTravessa.forEach(t => { vaosTravessa.push(t.top - anterior); anterior = t.top + d; });
        vaosTravessa.push(FA - d - anterior);
    } else {
        for (let k = 0; k < fixas.length - 1; k++) {
            const G = vaosFixas[k];
            const nk = Math.max(0, Math.ceil((G - REGRAS.VAO_TRAVESSA_MAX) / (REGRAS.VAO_TRAVESSA_MAX + d)));
            const passoK = (G - d * nk) / (nk + 1);
            vaosTravessa.push(passoK);
            for (let j = 1; j <= nk; j++) {
                const top = fixas[k].bottom + j * passoK + (j - 1) * d;
                itensTravessa.push({ top, centro: top + d / 2, friso: null, manual: false });
            }
            if (fixas[k + 1].friso) itensTravessa.push({ top: fixas[k + 1].top, centro: fixas[k + 1].top + d / 2, friso: fixas[k + 1].friso.indice, manual: false });
        }
    }
    let posicoes: number[] = itensTravessa.map(t => t.top);
    let n = posicoes.length;
    let passo = Math.max(...vaosTravessa);

    let cF = m.perfilFolha.cod, cB = m.perfilBatente.cod;
    let cortes: any[] = [
        { pos: 'F1', peca: 'Montante lateral (bordas da folha)', perfil: cF, qtd: 2, comprimento: FA, grupo: 'folha' }
    ];
    let internos = montantes.length - 2;
    if (internos > 0) cortes.push(
        { pos: 'F2', peca: 'Montante interno (cava / friso / extra)', perfil: cF, qtd: internos, comprimento: FA - 2 * d, grupo: 'folha' }
    );
    cortes.push(
        { pos: 'F3', peca: 'Travessa superior e inferior', perfil: cF, qtd: 2, comprimento: FL - 2 * d, grupo: 'folha' }
    );
    campos.forEach((z, k) => {
        cortes.push({ pos: 'F' + (4 + k), peca: 'Travessa intermediária — ' + z.nome, perfil: cF, qtd: n, comprimento: z.largura, grupo: 'folha' });
    });
    const marcoPecas = e.semMarco ? { cortes: [], acm: [], comprimentoCabecote: VL + FL + 80 } : marcoDaPorta(m, VL, VA, FL, cor('U'));
    cortes.push(...marcoPecas.cortes);

    // ---- Cortes verticais das chapas: a cava e cada friso vertical abrem um vão entre duas chapas
    const EXTRA: any = {
        lateral: REGRAS.BORDA_INFINITA, junta: REGRAS.BORDA_INFINITA,
        cava: REGRAS.BORDA_INFINITA, pegador: REGRAS.PEGADOR, topo: 0
    };
    type Vao = { x0: number; x1: number; fimChapa: string; iniChapa: string; friso: number | null };
    const vaosChapa: Vao[] = [];
    if (zCava) vaosChapa.push({ x0: zCava.x0 + CAVA.aba, x1: zCava.x1, fimChapa: 'pegador', iniChapa: 'cava', friso: null });
    verticais.forEach(f => vaosChapa.push({ x0: f.centro - f.friso / 2, x1: f.centro + f.friso / 2, fimChapa: 'junta', iniChapa: 'junta', friso: f.indice }));
    vaosChapa.sort((a, b) => a.x0 - b.x0);
    const bases: any[] = [];
    {
        let ini = REGRAS.RECUO_BORDA, bordaIni = 'lateral', frisoIni: number | null = null;
        vaosChapa.forEach(v => {
            bases.push({ x0: ini, x1: v.x0, bordaEsq: bordaIni, bordaDir: v.fimChapa, frisoEsq: frisoIni, frisoDir: v.friso });
            ini = v.x1; bordaIni = v.iniChapa; frisoIni = v.friso;
        });
        bases.push({ x0: ini, x1: FL - REGRAS.RECUO_BORDA, bordaEsq: bordaIni, bordaDir: 'lateral', frisoEsq: frisoIni, frisoDir: null });
    }

    // ---- Linhas de chapa: cada friso horizontal corta as chapas na altura
    type Linha = { y0: number; y1: number; cima: string; baixo: string; frisoCima: number | null; frisoBaixo: number | null };
    const linhas: Linha[] = [];
    {
        let y = 0, cima = 'passe', frisoCima: number | null = null;
        horizontais.forEach(f => {
            linhas.push({ y0: y, y1: f.centro - f.friso / 2, cima, baixo: 'friso', frisoCima, frisoBaixo: f.indice });
            y = f.centro + f.friso / 2; cima = 'friso'; frisoCima = f.indice;
        });
        linhas.push({ y0: y, y1: FA, cima, baixo: 'passe', frisoCima, frisoBaixo: null });
    }
    const passeFim = REGRAS.PASSE_ALTURA / 2;

    // Divisões manuais partem as chapas na largura
    const folgaDiv = DETALHES_PADRAO.FOLGA_DIVISAO;
    // Linhas em que a chapa não cabe inteira (correr): sem divisões informadas, parte em partes iguais
    const divisoesAuto = e.divisoes === undefined && !!m.divisaoAutomatica;
    if (divisoesAuto) {
        const lim: number = m.larguraMaxChapa;
        bases.forEach(b => {
            const face = b.x1 - b.x0, extra = Math.max(EXTRA[b.bordaEsq], EXTRA[b.bordaDir]);
            if (face + EXTRA[b.bordaEsq] + EXTRA[b.bordaDir] <= lim) return;
            let nPartes = 2;
            while (nPartes < 12 && (face - (nPartes - 1) * folgaDiv) / nPartes + extra > lim) nPartes++;
            const parte = (face - (nPartes - 1) * folgaDiv) / nPartes;
            for (let q = 1; q < nPartes; q++) divisoesIn.push(Math.round(b.x0 + q * (parte + folgaDiv) - folgaDiv / 2));
        });
        divisoesIn.sort((a, b) => a - b);
    }
    let divisoesAplicadas: number[] = [];
    let bandejas: any[] = [];
    bases.forEach((b, k) => {
        const dentro = divisoesIn.filter(x => x - folgaDiv / 2 > b.x0 && x + folgaDiv / 2 < b.x1);
        divisoesAplicadas = divisoesAplicadas.concat(dentro);
        let inicio = b.x0, bordaEsq = b.bordaEsq, frisoEsq = b.frisoEsq;
        const trechos: any[] = [];
        dentro.forEach(x => {
            trechos.push({ x0: inicio, x1: x - folgaDiv / 2, bordaEsq, bordaDir: 'topo', frisoEsq, frisoDir: null });
            inicio = x + folgaDiv / 2; bordaEsq = 'topo'; frisoEsq = null;
        });
        trechos.push({ x0: inicio, x1: b.x1, bordaEsq, bordaDir: b.bordaDir, frisoEsq, frisoDir: b.frisoDir });
        trechos.forEach((t, nT) => {
            linhas.forEach((ln, r) => {
                const altura = ln.y1 - ln.y0;
                bandejas.push({
                    indice: bandejas.length + 1,
                    origem: k + 1,
                    rotulo: (k + 1) + (nT === 0 ? '' : String.fromCharCode(97 + nT)) + (r === 0 ? '' : '.' + (r + 1)),
                    linha: r,
                    face: t.x1 - t.x0,
                    altura,
                    chapaLargura: t.x1 - t.x0 + EXTRA[t.bordaEsq] + EXTRA[t.bordaDir],
                    chapaComprimento: altura + (ln.cima === 'passe' ? passeFim : REGRAS.BORDA_INFINITA) + (ln.baixo === 'passe' ? passeFim : REGRAS.BORDA_INFINITA),
                    x0: t.x0, x1: t.x1, y0: ln.y0, y1: ln.y1,
                    bordaEsq: t.bordaEsq, bordaDir: t.bordaDir, bordaCima: ln.cima, bordaBaixo: ln.baixo,
                    frisoEsq: t.frisoEsq, frisoDir: t.frisoDir, frisoCima: ln.frisoCima, frisoBaixo: ln.frisoBaixo
                });
            });
        });
    });
    const colunas = bandejas.filter(p => p.linha === 0);

    // ---- Tipo de cada chapa: lisa, ripada (ripas dobradas em ACM) ou frisada (fresas decorativas)
    const normTipo = (t?: ChapaTipoEntrada): any => {
        if (t?.tipo === 'ripada') {
            const R = CHAPA_TIPOS_PADRAO.ripada;
            return { tipo: 'ripada' as TipoChapa, ripa: Math.max(10, num(t.ripa, R.ripa)), vao: Math.max(0, num(t.vao, R.vao)), prof: R.prof, fundo: t.fundo || '' };
        }
        if (t?.tipo === 'frisada') {
            const F = CHAPA_TIPOS_PADRAO.frisada;
            return { tipo: 'frisada' as TipoChapa, passo: Math.max(F.fresaMin, num(t.passo, F.passo)), direcao: t.direcao === 'horizontal' ? 'horizontal' as const : 'vertical' as const };
        }
        return { tipo: 'lisa' as TipoChapa };
    };
    const tiposChapa: Record<string, any> = {};
    ['A', 'B'].forEach(lado => bandejas.forEach(p => {
        // Linha ripada: sem escolha própria, toda chapa é ripada
        const spec = normTipo((e.chapaTipos && e.chapaTipos[lado + p.rotulo]) || e.chapaPadrao || (e.linhaRipada ? { tipo: 'ripada' } : undefined));
        const info: any = { ...spec, lado, rotulo: p.rotulo };
        if (spec.tipo === 'ripada') {
            const n = Math.max(1, Math.floor((p.face + spec.vao) / (spec.ripa + spec.vao)));
            const ocupado = n * spec.ripa + (n - 1) * spec.vao;
            const ini = p.x0 + (p.face - ocupado) / 2;
            info.n = n;
            info.desenvolvimento = spec.ripa + 2 * spec.prof + CHAPA_TIPOS_PADRAO.ripada.abas;
            info.posicoes = Array.from({ length: n }, (_, i) => ini + i * (spec.ripa + spec.vao));
            info.cabe = p.face >= spec.ripa;
        } else if (spec.tipo === 'frisada') {
            const ini = spec.direcao === 'vertical' ? p.x0 : p.y0, fim = spec.direcao === 'vertical' ? p.x1 : p.y1;
            info.posicoes = [];
            for (let v = ini + spec.passo; v < fim - 10; v += spec.passo) info.posicoes.push(v);
            info.linhas = info.posicoes.length;
        }
        tiposChapa[lado + p.rotulo] = info;
    }));

    let acm: any[] = [], slots: string[] = [];
    const descBorda: any = {
        lateral: 'BI lateral', junta: 'BI na junta',
        cava: 'BI na boca da cava', pegador: 'PEGADOR (transpasse 40)',
        topo: 'divisão de topo (corte reto)'
    };
    ['A', 'B'].forEach(lado => {
        bandejas.forEach((p) => {
            // A cor é da chapa de origem (A1, A2...): as partes de uma divisão (A1, A1b) e as linhas de um friso
            // horizontal (A1.2) seguem a mesma cor, a não ser que a parte tenha cor própria (cores.A1b, cores.A1.2).
            let slot = lado + p.origem;
            if (slots.indexOf(slot) < 0) slots.push(slot);
            const corParte = (e.cores && e.cores[lado + p.rotulo]) || cor(slot);
            const tc = tiposChapa[lado + p.rotulo];
            const obsBase = descBorda[p.bordaEsq] + ' + ' + descBorda[p.bordaDir]
                + (p.bordaCima === 'friso' || p.bordaBaixo === 'friso' ? ' + BI no friso horizontal' : '') + ' · comprimento reto';
            const entrada: any = {
                pos: lado + p.rotulo, peca: 'Bandeja lado ' + lado + ' — ' + p.face + ' de face',
                slot: slot, cor: corParte, corComercial: nomeComercial(corParte), qtd: 1,
                chapaLargura: p.chapaLargura, chapaComprimento: p.chapaComprimento,
                obs: obsBase, grupo: 'folha', tipoChapa: tc.tipo
            };
            if (tc.tipo === 'ripada') {
                // A face vira n ripas dobradas (face + 2 laterais + abas de fixação), no comprimento da chapa
                entrada.peca = `Ripas lado ${lado} — ${tc.n} × ${tc.ripa} (chapa ${p.rotulo})`;
                entrada.qtd = tc.n;
                entrada.chapaLargura = tc.desenvolvimento;
                entrada.obs = `ripa ${tc.ripa} · vão ${tc.vao} · desenvolvimento ${tc.desenvolvimento} (face + 2×${tc.prof} + ${CHAPA_TIPOS_PADRAO.ripada.abas} de abas) · comprimento reto · coladas na chapa de fundo`;
                // Chapa de fundo: a bandeja de sempre (mesmas bordas), onde as ripas são coladas; aparece nos vãos entre elas
                const corFundo = tc.fundo || corParte;
                acm.push({
                    pos: lado + p.rotulo + 'F', peca: `Fundo da ripada lado ${lado} — ${p.face} de face (chapa ${p.rotulo})`,
                    slot: slot, cor: corFundo, corComercial: nomeComercial(corFundo), qtd: 1,
                    chapaLargura: p.chapaLargura, chapaComprimento: p.chapaComprimento,
                    obs: obsBase + ' · FUNDO: as ripas são coladas aqui (fita dupla-face)', grupo: 'folha', tipoChapa: 'ripada'
                });
            } else if (tc.tipo === 'frisada') {
                entrada.obs = obsBase + ` · FRISADA: ${tc.linhas} fresa(s) ${tc.direcao === 'vertical' ? 'na altura' : 'na largura'}, a cada ${tc.passo}`;
            }
            acm.push(entrada);
        });
    });

    // Puxador integrado da ripada: a primeira ripa da primeira faixa ganha um trecho alto (60 de profundidade)
    let puxadorRipaInfo: any = null;
    const puxadorLigado = !fixa && (e.puxadorRipa ?? !!e.linhaRipada);
    const primeira = bandejas.find(p => p.origem === 1 && p.linha === 0);
    if (puxadorLigado && primeira) {
        const tA = tiposChapa['A' + primeira.rotulo], tB = tiposChapa['B' + primeira.rotulo];
        if (tA.tipo === 'ripada' || tB.tipo === 'ripada') {
            const R = CHAPA_TIPOS_PADRAO.ripada;
            const baseP = Math.min(100, FA * 0.1), topoP = Math.min(FA * 0.62, 1900); // do piso, em mm
            const comprimento = Math.round(topoP - baseP);
            const ladoRef = tA.tipo === 'ripada' ? tA : tB;
            puxadorRipaInfo = {
                x0: ladoRef.posicoes[0], ripa: ladoRef.ripa, profundidade: PUXADOR_RIPA_PROF,
                yTopo: FA - topoP, yBase: FA - baseP, comprimento,
                lados: [tA.tipo === 'ripada' ? 'A' : null, tB.tipo === 'ripada' ? 'B' : null].filter(Boolean)
            };
            (puxadorRipaInfo.lados as string[]).forEach(lado => {
                const slot = lado + primeira.origem;
                const corPux = (e.cores && e.cores[lado + primeira.rotulo]) || cor(slot);
                acm.push({
                    pos: 'PX' + lado, peca: `Puxador integrado (ripa alta) lado ${lado}`,
                    slot, cor: corPux, corComercial: nomeComercial(corPux), qtd: 1,
                    chapaLargura: ladoRef.ripa + 2 * PUXADOR_RIPA_PROF + R.abas, chapaComprimento: comprimento,
                    obs: `ripa alta sobre a ripa 1 (profundidade ${PUXADOR_RIPA_PROF}) · trecho de ${comprimento} a partir de ${Math.round(baseP)} do piso`,
                    grupo: 'folha', tipoChapa: 'ripada'
                });
            });
        }
    }

    let peca02: any = null;
    // O puxador cava para onde a chapa para: cada friso horizontal corta a peça, e cada trecho é uma Peça 02 própria
    const cavaTrechos: Array<{ linha: number; y0: number; y1: number; altura: number; chapa: { largura: number; comprimento: number } }> = [];
    if (zCava) {
        peca02 = calcularPeca02(FA, cVao);
        linhas.forEach((ln, r) => {
            const altura = ln.y1 - ln.y0;
            cavaTrechos.push({ linha: r, y0: ln.y0, y1: ln.y1, altura, chapa: calcularPeca02(altura, cVao).chapa });
        });
        ['A', 'B'].forEach(lado => {
            let slot = 'cava' + lado;
            if (slots.indexOf(slot) < 0) slots.push(slot);
            cavaTrechos.forEach((t, r) => {
                const p02 = calcularPeca02(t.altura, cVao);
                acm.push({
                    pos: 'C' + lado + (r === 0 ? '' : '.' + (r + 1)),
                    peca: 'Peça 02 — Puxador Cava lado ' + lado + (cavaTrechos.length > 1 ? ` — trecho ${r + 1} de ${cavaTrechos.length} (cortado no friso)` : ''),
                    slot: slot, cor: cor(slot), corComercial: nomeComercial(cor(slot)), qtd: 1,
                    chapaLargura: p02.chapa.largura, chapaComprimento: p02.chapa.comprimento,
                    obs: p02.fresasLargura.join('·') + ' / ' + p02.fresasComprimento.join('·'),
                    grupo: 'folha'
                });
            });
        });
    }

    // Fundo de cada friso: colado direto no alumínio, um por lado (rebaixa o friso em 3)
    const segmentosHoriz: Array<[number, number]> = zCava
        ? [[REGRAS.RECUO_BORDA, zCava.x0], [zCava.x1, FL - REGRAS.RECUO_BORDA]]
        : [[REGRAS.RECUO_BORDA, FL - REGRAS.RECUO_BORDA]];
    frisos.forEach(f => {
        if (slots.indexOf(f.slot) < 0) slots.push(f.slot);
        const nomeF = frisos.length === 1 ? 'Peça 02 — Fundo da junta (1 por lado)'
            : `Peça 02 — Fundo do friso ${f.indice + 1}, ${f.orientacao} (1 por lado)`;
        const base = f.indice === 0 ? 'J' : 'J' + (f.indice + 1);
        const obs = 'sem BI · colada direto no alumínio (rebaixa o friso em 3) · aparece ' + f.friso;
        const peca = (pos: string, nome: string, comprimento: number) => acm.push({
            pos, peca: nome, slot: f.slot, cor: f.cor, corComercial: nomeComercial(f.cor), qtd: 2,
            chapaLargura: f.fundoChapa, chapaComprimento: comprimento, obs, grupo: 'folha'
        });
        if (f.orientacao === 'vertical') peca(base, nomeF, FA + REGRAS.PASSE_ALTURA);
        else segmentosHoriz.forEach(([a, b], k) => peca(segmentosHoriz.length > 1 ? base + 'abc'[k] : base, nomeF + (segmentosHoriz.length > 1 ? ` — trecho ${k + 1}` : ''), b - a));
    });

    if (slots.indexOf('U') < 0) slots.push('U');
    if (!fixa) acm.push({
        pos: 'P1', peca: 'Peça 01 — U de Revestimento de Borda (' + m.uBorda.nome + ')' + (m.tipoMarco === 'cabecote' ? ' — laterais' : ''),
        slot: 'U', cor: cor('U'), corComercial: nomeComercial(cor('U')), qtd: 2,
        chapaLargura: m.uBorda.desenv, chapaComprimento: FA + REGRAS.PASSE_ALTURA,
        obs: 'U fecha justo ao perfil (tubo + 7)', grupo: 'folha'
    });
    // Correr: as quatro bordas da folha ficam à vista, então o U também reveste topo e base
    if (m.tipoMarco === 'cabecote' && !fixa) acm.push({
        pos: 'P2', peca: 'Peça 01 — U de Revestimento de Borda (' + m.uBorda.nome + ') — topo e base',
        slot: 'U', cor: cor('U'), corComercial: nomeComercial(cor('U')), qtd: 2,
        chapaLargura: m.uBorda.desenv, chapaComprimento: FL,
        obs: 'U fecha justo ao perfil (tubo + 7)', grupo: 'folha'
    });

    acm.push(...marcoPecas.acm);
    const comprimentoCabecote = marcoPecas.comprimentoCabecote;

    let linF = 0, linB = 0, linFolha = 0;
    cortes.forEach(c => {
        if (c.perfil === cF) linF += c.qtd * c.comprimento;
        if (c.perfil === cB) linB += c.qtd * c.comprimento;
        if (c.grupo === 'folha') linFolha += c.qtd * c.comprimento;
    });

    let planF = planoBarras(cortes, cF), planB = planoBarras(cortes, cB, REGRAS.KERF_BATENTE);
    let areaFolha = 0;
    acm.forEach(p => {
        if (p.grupo === 'folha') areaFolha += p.qtd * p.chapaLargura * p.chapaComprimento / 1e6;
    });

    let pesoAluminio = linF / 1000 * m.perfilFolha.kgm + linB / 1000 * m.perfilBatente.kgm;
    let pesoFolha = linFolha / 1000 * m.perfilFolha.kgm + areaFolha * REGRAS.ACM_KG_M2;
    let dobradicas = m.ferragem === 'dobradica' ? (FA > 2400 ? 4 : 3) : 0;
    let ferragem = fixa ? { tipo: 'fixa', quantidade: 0, cargaMinima: 0 } : m.ferragem === 'pivo'
        ? { tipo: 'pivô', quantidade: 1, cargaMinima: Math.ceil(pesoFolha * REGRAS.FATOR_FERRAGEM / 10) * 10 }
        : m.ferragem === 'roldana'
            ? { tipo: 'roldana', quantidade: 2, cargaMinima: Math.ceil(pesoFolha * REGRAS.FATOR_FERRAGEM / 2) }
            : { tipo: 'dobradiça', quantidade: dobradicas, cargaMinima: Math.ceil(pesoFolha * REGRAS.FATOR_FERRAGEM / dobradicas) };

    let somaFace = 0; colunas.forEach(p => { somaFace += p.face; });
    let somaVaos = (zCava ? cVao - CAVA.aba : 0) + verticais.reduce((a, f) => a + f.friso, 0);
    let maiorPerfil = 0; cortes.forEach(c => { maiorPerfil = Math.max(maiorPerfil, c.comprimento); });
    let maiorChapa = 0; bandejas.forEach(p => { maiorChapa = Math.max(maiorChapa, p.chapaLargura); });

    let conferencias: any[] = [
        { chave: 'largura_fecha', descricao: 'Largura fecha (4 + faces + vãos + 4 = folha)',
          ok: 2 * REGRAS.RECUO_BORDA + somaFace + somaVaos + divisoesAplicadas.length * folgaDiv === FL },
        { chave: 'altura_limite', descricao: `Altura dentro do limite da linha (${m.alturaMin} a ${m.alturaMax})`,
          ok: VA <= m.alturaMax && VA >= m.alturaMin },
        { chave: 'vao_travessa', descricao: `Vão entre travessas ≤ ${REGRAS.VAO_TRAVESSA_MAX}`, ok: passo <= REGRAS.VAO_TRAVESSA_MAX, severidade: 'padrao' },
        { chave: 'campos_positivos', descricao: 'Todos os campos existem (> 0)',
          ok: campos.every(z => z.largura > 0) && !sobrepoe },
        { chave: 'perfil_na_barra', descricao: 'Nenhuma peça de perfil > barra de 6 m (acima disso leva emenda e barra a mais)', ok: maiorPerfil <= REGRAS.BARRA, severidade: 'padrao' },
        { chave: 'chapa_disponivel', descricao: `Bandeja maior cabe na chapa (≤ ${(m.larguraMaxChapa as number).toLocaleString('pt-BR')})`, ok: maiorChapa <= m.larguraMaxChapa },
        { chave: 'fechadura_na_folha', descricao: 'Fechadura dentro da folha', ok: FE < FA }
    ];
    // Painel fixo: não tem fechadura nem limite de altura de porta
    if (fixa) conferencias = conferencias.filter(c => c.chave !== 'fechadura_na_folha' && c.chave !== 'altura_limite');
    // Conferências de edição: 'erro' = geometria que não fecha; 'padrao' = fora do que o Método ACM validou
    conferencias.forEach(c => { c.severidade = c.severidade || 'erro'; });
    const padrao = (chave: string, descricao: string, ok: boolean) => conferencias.push({ chave, descricao, ok, severidade: 'padrao' });
    const erro = (chave: string, descricao: string, ok: boolean) => conferencias.push({ chave, descricao, ok, severidade: 'erro' });
    erro('travessa_cabe', 'Travessas cabem entre as da borda, sem se sobrepor', vaosTravessa.every(v => v >= 0));
    erro('chapa_minima', `Toda chapa tem ao menos ${DETALHES_PADRAO.FACE_MINIMA} de face`,
        bandejas.every(p => Math.min(p.face, p.altura) >= DETALHES_PADRAO.FACE_MINIMA));
    const diferentes = Object.values(tiposChapa).filter((t: any) => t.tipo !== 'lisa');
    if (diferentes.length > 0) {
        padrao('chapa_tipo_padrao', 'Chapas ripadas/frisadas: medidas de referência, ainda não validadas pelo Método ACM', false);
        erro('ripada_cabe', 'Cada chapa ripada comporta ao menos uma ripa', diferentes.every((t: any) => t.tipo !== 'ripada' || t.cabe));
    }
    if (zCava) {
        erro('cava_cabe', `Vão da cava comporta as duas abas (≥ ${2 * CAVA.aba})`, cVao >= 2 * CAVA.aba);
        padrao('cava_padrao', `Cava no padrão validado (vão ${CAVA.vao}, ${DETALHES_PADRAO.CAVA_AFASTAMENTO} de campo antes do montante)`,
            cVao === CAVA.vao && cAfast === DETALHES_PADRAO.CAVA_AFASTAMENTO);
    }
    if (frisos.length > 0) {
        erro('friso_fecha', frisos.length === 1
            ? `Friso = vão da junta − 2 × avanço (${frisos[0].vaoLivre} − 2×${frisos[0].avanco})`
            : 'Cada friso cabe no vão da junta (friso ≤ vão)',
            frisos.every(f => f.friso > 0 && f.vaoLivre >= f.friso));
        padrao('junta_padrao', `Friso no padrão da linha (um friso vertical, vão ${m.junta.vao}, friso ${m.junta.friso}, a ${m.junta.faixa} da borda)`,
            // Frisos automáticos (união de chapas) não contam: só os que a pessoa definiu
            (() => {
                const fm = frisos.filter(f => !f.auto);
                return fm.length === 0 || (fm.length === 1 && fm[0].orientacao === 'vertical'
                    && fm[0].vaoLivre === frisoPadraoLegado.vao && fm[0].friso === frisoPadraoLegado.friso
                    && Math.abs(fm[0].posicao - frisoPadraoLegado.posicao) < 1e-6);
            })());
    }
    if (horizontais.length > 0) {
        erro('friso_horizontal_cabe', `Frisos horizontais cabem entre as travessas da borda (vão ≥ ${DETALHES_PADRAO.FACE_MINIMA})`,
            vaosFixas.every(v => v >= DETALHES_PADRAO.FACE_MINIMA));
        if (zCava) padrao('friso_cruza_cava', 'Friso horizontal não cruza a cava (cava e friso sem encontro)', false);
        if (verticais.length > 0) padrao('friso_cruza_friso', 'Frisos horizontais e verticais não se cruzam', false);
    }
    if (divisoesIn.length > 0) {
        erro('divisao_valida', 'Divisões de chapa caem dentro de uma chapa (não na cava, na junta ou fora da folha)', divisoesAplicadas.length === divisoesIn.length);
        // As divisões automáticas (chapa mais larga que a chapa comercial) não entram: são o padrão da linha
        padrao('divisao_apoio', 'Divisões de chapa sobre um montante (apoio no perfil)',
            divisoesAuto || divisoesAplicadas.every(x => montantes.some(z => x >= z.x0 && x <= z.x1)));
    }

    if (zCava) conferencias.splice(1, 0, {
        chave: 'peca02_altura', descricao: 'Peça 02 fecha na altura (140 + central + 140)',
        ok: peca02!.conferenciaAltura === FA, severidade: 'erro'
    });

    // Junta "legada": o primeiro friso vertical, no formato que as telas antigas leem
    const primeiro = verticais[0];
    const junta = primeiro ? {
        vaoLivre: primeiro.vaoLivre, friso: primeiro.friso, avanco: primeiro.avanco,
        faixa: faixaDoCentro(primeiro.centro, primeiro.vaoLivre),
        fundoChapa: primeiro.fundoChapa, rebaixo: REGRAS.NIVELAMENTO, referencia: m.junta.referencia
    } : null;

    return {
        versao: '3.2',
        entrada: { modelo: m.id, vaoLargura: VL, vaoAltura: VA, fechaduraEixo: FE,
                   cava: cavaOn, junta: frisos.length > 0, projeto: e.projeto || '', cores,
                   cavaAfastamento: cAfast, cavaVao: cVao, divisoes: divisoesIn, divisoesAuto, montantesExtras: extrasIn,
                   travessas: travessaManual ? itensTravessa.filter(t => t.friso == null).map(t => FA - t.centro) : null,
                   frisos: frisos.map(f => ({ orientacao: f.orientacao, posicao: f.posicao, vao: f.vaoLivre, friso: f.friso, ...(f.corExplicita ? { cor: f.corExplicita } : {}), ...(f.auto ? { auto: true } : {}) })) },
        modelo: { id: m.id, linha: m.linha, nome: e.linhaRipada ? 'Porta pivotante ripada' : m.nome,
                  titulo: e.linhaRipada ? 'PORTA PIVOTANTE RIPADA EM ACM' : m.titulo, ripada: !!e.linhaRipada, validado: m.validado,
                  perfilFolha: m.perfilFolha, perfilBatente: m.perfilBatente, d: m.d,
                  descontoLargura: m.descontoLargura, descontoAltura: m.descontoAltura,
                  btL: m.btL, btT: m.btT, folgaInf: m.folgaInf, vergaDesconto: m.vergaDesconto,
                  tipoMarco: m.tipoMarco, ferragem: m.ferragem, larguraMaxChapa: m.larguraMaxChapa },
        cabecote: m.tipoMarco === 'cabecote'
            ? { comprimento: comprimentoCabecote, altura: CORRER_DIM.cabecoteAltura, prof: CORRER_DIM.cabecoteProf, abreParaDireita: (e.ladoAbertura || '').toLowerCase().startsWith('dir') }
            : null,
        folha: { largura: FL, altura: FA,
                 descontoLargura: m.descontoLargura, descontoAltura: m.descontoAltura },
        zonas: Z,
        travessas: { quantidade: n, vaoLivre: passo, vaos: vaosTravessa, posicoes: posicoes, itens: itensTravessa, manual: travessaManual },
        frisos: frisos,
        junta: junta,
        cava: zCava ? peca02 : null,
        cavaTrechos: cavaTrechos,
        corteAluminio: cortes,
        planoBarras: { [cF]: planF, [cB]: planB },
        bandejas: bandejas,
        tiposChapa: tiposChapa,
        puxadorRipa: puxadorRipaInfo,
        pecasACM: acm,
        posicoesDeCor: slots,
        resumo: {
            linearFolha: linF, linearBatente: linB,
            barras: { [cF]: planF.length, [cB]: planB.length },
            pesoAluminio: pesoAluminio, areaACMFolha: areaFolha, pesoFolha: pesoFolha,
            ferragem: ferragem
        },
        conferencias: conferencias,
        valido: conferencias.every(c => c.ok)
    };
}

// ------------------------------------------------------------------ chapa comercial e frisos automáticos

/** Larguras de chapa de ACM disponíveis (todas com 5000 de comprimento). */
export const LARGURAS_CHAPA = [1220, 1250, 1500] as const;

/** Espessura válida (3 ou 4 mm) escolhida à mão; qualquer outro valor = automática. */
export function espessuraChapaValida(v: unknown): 3 | 4 | undefined {
    const n = Number(v);
    return n === 3 || n === 4 ? n : undefined;
}

/** Espessura da chapa pela altura do vão: 3 mm até 4 m; 4 mm acima. */
export const espessuraChapaPorAltura = (vaoAltura: number): 3 | 4 => (vaoAltura > 4000 ? 4 : 3);

/**
 * Chapa comercial da porta. A espessura vem da escolha do orçamento (`espessuraEscolhida`) ou, sem ela, da altura do
 * vão (3 mm até 4 m; 4 mm acima). A largura padrão acompanha a espessura: 1220×5000 na de 3 mm, 1250×5000 na de 4 mm.
 * `larguraEscolhida` troca a largura (ex.: 1500) sem mudar a espessura.
 */
export function chapaComercial(vaoAltura: number, larguraEscolhida?: number, espessuraEscolhida?: number) {
    const espessura = espessuraChapaValida(espessuraEscolhida) ?? espessuraChapaPorAltura(vaoAltura);
    const padrao = espessura === 4 ? 1250 : 1220;
    const largura = larguraEscolhida && (LARGURAS_CHAPA as readonly number[]).includes(larguraEscolhida) ? larguraEscolhida : padrao;
    return { espessura, largura, comprimento: 5000 };
}

type ChapaCom = ReturnType<typeof chapaComercial>;

/** A peça não cabe na chapa (nem girada). */
const foraDaChapaComercial = (largura: number, comprimento: number, ch: { largura: number; comprimento: number }) =>
    !((largura <= ch.largura && comprimento <= ch.comprimento) || (comprimento <= ch.largura && largura <= ch.comprimento));

/** Máximo de frisos que o motor insere sozinho por porta. */
const MAX_FRISOS_AUTO = 10;

/** Acréscimo do puxador cava sobre a altura do trecho (dobras das pontas): comprimento da peça = altura + 76. */
const ACRESCIMO_CAVA = calcularPeca02(1000).chapa.comprimento - 1000;

/**
 * Menor face que o friso automático deixa na chapa que sobra: o montante do friso (meio montante de cada lado do centro)
 * mais o montante da borda e a face mínima, para o vão da estrutura continuar válido. Nunca menos de 150.
 */
const sobraMinimaFriso = (m: any, friso: number) => Math.max(150, Math.ceil(m.d * 1.5 + DETALHES_PADRAO.FACE_MINIMA - friso / 2 + 10));

/** Acréscimo de cada tipo de borda sobre a face da bandeja (mesma tabela do cálculo das bandejas). */
const EXTRA_BORDA: Record<string, number> = { lateral: REGRAS.BORDA_INFINITA, junta: REGRAS.BORDA_INFINITA, cava: REGRAS.BORDA_INFINITA, pegador: REGRAS.PEGADOR, topo: 0 };

/** Peças que o friso consegue dividir e que precisam caber na chapa: as bandejas e os trechos do puxador cava. */
function chapasDaPorta(d: any): Array<{ largura: number; comprimento: number }> {
    return [
        ...(d.bandejas as any[]).map(b => ({ largura: b.chapaLargura, comprimento: b.chapaComprimento })),
        ...((d.cavaTrechos || []) as any[]).map(t => ({ largura: t.chapa.largura, comprimento: t.chapa.comprimento })),
    ];
}
const chapasForaDaComercial = (d: any, ch: ChapaCom) => chapasDaPorta(d).filter(p => foraDaChapaComercial(p.largura, p.comprimento, ch));

/**
 * Próximo friso para unir as chapas. O friso fica no LIMITE da chapa (não no meio): a primeira chapa usa a largura
 * (ou o comprimento) máxima e a que sobra fica do outro lado, com a sobra mínima de face (`sobraMinimaFriso`).
 * - Bandeja mais larga que a comercial: friso vertical, a partir da borda do puxador/fechadura.
 * - Bandeja ou trecho do cava mais comprido que a comercial: friso horizontal, a partir da base (a chapa de baixo
 *   e o trecho do cava de baixo saem inteiros; o cava para onde a chapa para).
 * Devolve null quando todas cabem.
 */
function proximoFrisoAuto(d: any, m: any, ch: ChapaCom): FrisoEntrada | null {
    const fora = (d.bandejas as any[]).filter(b => foraDaChapaComercial(b.chapaLargura, b.chapaComprimento, ch));
    const foraCava = ((d.cavaTrechos || []) as any[]).filter(t => foraDaChapaComercial(t.chapa.largura, t.chapa.comprimento, ch));
    if (fora.length === 0 && foraCava.length === 0) return null;
    const friso: number = m.junta.friso;
    const base = { vao: m.junta.vao, friso, auto: true };
    const minimo = DETALHES_PADRAO.FACE_MINIMA;

    const largas = fora.filter(b => b.chapaLargura > ch.largura && b.chapaComprimento > ch.largura);
    if (largas.length > 0) {
        const b = largas.reduce((a, x) => (x.chapaLargura > a.chapaLargura ? x : a));
        const face = b.x1 - b.x0;
        const limiteFace = ch.largura - (EXTRA_BORDA[b.bordaEsq] ?? 0) - EXTRA_BORDA.junta;
        let primeira = Math.min(limiteFace, face - friso - sobraMinimaFriso(m, friso));
        if (primeira < minimo) primeira = (face - friso) / 2;
        return { orientacao: 'vertical', posicao: Math.floor(b.x0 + primeira + friso / 2), ...base };
    }

    // Altura: a linha de chapa mais alta entre as bandejas e os trechos do cava que não cabem
    const linhasFora = new Set<number>([...fora.map(b => b.linha as number), ...foraCava.map(t => t.linha as number)]);
    const b = (d.bandejas as any[]).filter(x => linhasFora.has(x.linha)).reduce((a, x) => (x.altura > a.altura ? x : a));
    const extrasBaixo = (b.bordaBaixo === 'passe' ? REGRAS.PASSE_ALTURA / 2 : REGRAS.BORDA_INFINITA) + REGRAS.BORDA_INFINITA;
    let maximo = ch.comprimento - extrasBaixo;
    if ((d.cavaTrechos || []).length > 0) maximo = Math.min(maximo, ch.comprimento - ACRESCIMO_CAVA);
    let embaixo = Math.min(maximo, b.altura - friso - sobraMinimaFriso(m, friso));
    if (embaixo < minimo) embaixo = (b.altura - friso) / 2;
    const centroDoTopo = b.y1 - embaixo - friso / 2;
    return { orientacao: 'horizontal', posicao: Math.floor(d.folha.altura - centroDoTopo), ...base };
}

/** Excesso de largura (soma do que passa da chapa) nas chapas da porta. */
const excessoLargura = (d: any, ch: ChapaCom) => chapasDaPorta(d).reduce((a, p) => a + Math.max(0, p.largura - ch.largura), 0);

/**
 * O puxador cava já parte a bandeja, então é uma emenda: antes de pensar em friso, o motor o desloca (só quando a
 * pessoa não o posicionou) o mínimo necessário para a chapa do outro lado sair cheia (largura da chapa). Se a cava
 * chegasse ao limite da chapa do lado do puxador e ainda sobrasse chapa grande, o resto fica para os frisos.
 */
function comCavaEmenda<T>(semAuto: any, d00: T, ch: ChapaCom): { entrada: any; d: T; deslocamento: number } {
    const d0: any = d00;
    const sem = { entrada: semAuto, d: d00, deslocamento: 0 };
    if (!d0.cava || !(d0.zonas as any[]).some(z => z.tipo === 'cava')) return sem;
    const grande = (d0.bandejas as any[]).find(b => b.bordaEsq === 'cava' && b.chapaLargura > ch.largura);
    const doPuxador = (d0.bandejas as any[]).find(b => b.bordaDir === 'pegador');
    if (!grande || !doPuxador) return sem;
    const alvo = ch.largura - (EXTRA_BORDA[grande.bordaEsq] ?? 0) - (EXTRA_BORDA[grande.bordaDir] ?? 0);
    const folgaDoPuxador = ch.largura - doPuxador.chapaLargura; // quanto a chapa do puxador ainda pode crescer
    const deslocamento = Math.ceil(Math.min((grande.x1 - grande.x0) - alvo, folgaDoPuxador));
    if (deslocamento <= 0) return sem;
    const entrada = { ...semAuto, cavaAfastamento: d0.entrada.cavaAfastamento + deslocamento };
    const d = calcularPortaBase(entrada);
    return excessoLargura(d, ch) < excessoLargura(d0, ch) ? { entrada, d: d as unknown as T, deslocamento } : sem;
}

/** Insere frisos até todas as chapas (bandejas e cava) caberem na chapa `ch` (ou até não haver mais ganho). */
function comFrisosAutomaticos<T>(semAuto: any, d0: T, m: any, ch: ChapaCom): T {
    let d: any = d0;
    for (let i = 0; i < MAX_FRISOS_AUTO; i++) {
        const novo = proximoFrisoAuto(d, m, ch);
        if (!novo) break;
        const proximo = calcularPortaBase({ ...semAuto, frisos: [...d.entrada.frisos, novo] });
        // Se o friso não ajudou (o excesso na dimensão que ele resolve não diminuiu), para: fica o aviso
        const vertical = novo.orientacao === 'vertical';
        const excesso = (x: any) => chapasDaPorta(x).reduce((a, p) => a + (vertical ? Math.max(0, p.largura - ch.largura) : Math.max(0, p.comprimento - ch.comprimento)), 0);
        if (excesso(proximo) >= excesso(d)) break;
        d = proximo;
    }
    return d as T;
}

/** Chapas inteiras e m² comprados para todas as peças da porta, por cor. */
function compraDaPorta(d: any, ch: ChapaCom) {
    const porCor = new Map<string, PecaAcm[]>();
    (d.pecasACM as any[]).forEach(x => {
        const l = porCor.get(x.cor) || [];
        l.push({ largura: x.chapaLargura, comprimento: x.chapaComprimento, qtd: x.qtd, cor: x.cor });
        porCor.set(x.cor, l);
    });
    let chapas = 0;
    porCor.forEach(l => { chapas += chapasNecessarias(l, ch.largura, ch.comprimento); });
    return { chapas, areaM2: chapas * ch.largura * ch.comprimento / 1e6 };
}

export interface OpcaoChapa {
    largura: number;
    comprimento: number;
    espessura: number;
    /** Frisos que o motor precisa inserir com esta chapa. */
    frisosAuto: number;
    /** Quanto o motor deslocou a cava (mm) para ela servir de emenda; 0 = cava no lugar. */
    cavaDeslocada: number;
    /** Chapas inteiras a comprar (por cor) e a área correspondente. */
    chapas: number;
    areaM2: number;
    /** Todas as chapas das bandejas cabem. */
    cabe: boolean;
    recomendada: boolean;
    escolhida: boolean;
}

/**
 * Melhor chapa: a que cabe, com menos frisos (menos juntas na porta); empatando, a de menor área comprada.
 * A diferença de área só vale se passar de 5%, senão fica a chapa padrão (a que tem em estoque).
 */
function melhorOpcao<T extends { cabe: boolean; frisosAuto: number; areaM2: number; ch: ChapaCom }>(opcoes: T[], padrao: number): T {
    const ordenadas = [...opcoes].sort((a, b) => {
        if (a.cabe !== b.cabe) return a.cabe ? -1 : 1;
        if (a.frisosAuto !== b.frisosAuto) return a.frisosAuto - b.frisosAuto;
        if (a.areaM2 < b.areaM2 * 0.95) return -1;
        if (b.areaM2 < a.areaM2 * 0.95) return 1;
        return (a.ch.largura === padrao ? 0 : 1) - (b.ch.largura === padrao ? 0 : 1);
    });
    return ordenadas[0];
}

export function calcularPorta(entrada: CalcularPortaParams) {
    const e = normalizarParams(entrada);
    const manuais = (f: FrisoEntrada[]) => f.filter(x => !x.auto);
    // Os frisos automáticos de uma especificação salva são recalculados do zero; só os manuais valem como entrada
    const semAuto = e && Array.isArray(e.frisos) ? { ...e, frisos: manuais(e.frisos) } : e;
    const d0 = calcularPortaBase(semAuto);
    const m = MODELOS[d0.entrada.modelo];
    const VA = d0.entrada.vaoAltura;
    const esp = espessuraChapaValida(e?.espessuraChapa);
    const padrao = chapaComercial(VA, undefined, esp).largura;
    const escolhida = e?.larguraChapa && (LARGURAS_CHAPA as readonly number[]).includes(e.larguraChapa) ? e.larguraChapa : undefined;
    const pula = e?.frisoAuto === false || d0.modelo.ripada || m.id === 'correr'
        || Object.values(d0.tiposChapa || {}).some((t: any) => t.tipo !== 'lisa');

    // Cada largura de chapa é avaliada: frisos necessários, chapas a comprar e se tudo cabe
    const larguras = [...new Set([padrao, 1500, ...(escolhida ? [escolhida] : [])])];
    const avaliadas = larguras.map(largura => {
        const ch = chapaComercial(VA, largura, esp);
        // A cava é uma emenda: entra na conta antes dos frisos (se a pessoa a posicionou à mão, fica onde está)
        const cavaEmenda = pula || e?.cavaAfastamento != null ? { entrada: semAuto, d: d0, deslocamento: 0 } : comCavaEmenda(semAuto, d0, ch);
        const d = pula ? d0 : comFrisosAutomaticos(cavaEmenda.entrada, cavaEmenda.d, m, ch);
        const cabe = chapasForaDaComercial(d, ch).length === 0;
        return { ch, d, cabe, cavaDeslocada: cavaEmenda.deslocamento, frisosAuto: (d.frisos as any[]).filter(f => f.auto).length, ...compraDaPorta(d, ch) };
    });
    const melhor = pula ? avaliadas.find(o => o.ch.largura === padrao)! : melhorOpcao(avaliadas, padrao);
    const usada = (escolhida && avaliadas.find(o => o.ch.largura === escolhida)) || melhor;

    const d = Object.assign(usada.d, { chapa: usada.ch, chapaOpcoes: [] as OpcaoChapa[], cavaEmenda: usada.cavaDeslocada > 0 ? { deslocamento: usada.cavaDeslocada, afastamento: usada.d.entrada.cavaAfastamento as number } : null });
    d.chapaOpcoes = avaliadas.map(o => ({
        largura: o.ch.largura, comprimento: o.ch.comprimento, espessura: o.ch.espessura, frisosAuto: o.frisosAuto, cavaDeslocada: o.cavaDeslocada,
        chapas: o.chapas, areaM2: o.areaM2, cabe: o.cabe, recomendada: o === melhor, escolhida: o === usada,
    }));
    // Conferência: toda chapa das bandejas cabe na comercial (as bandejas são o que o friso consegue dividir)
    d.conferencias.push({
        chave: 'chapa_comercial', severidade: 'padrao', ok: usada.cabe,
        descricao: `Chapas (bandejas e puxador cava) cabem na chapa comercial (${usada.ch.largura.toLocaleString('pt-BR')} × ${usada.ch.comprimento.toLocaleString('pt-BR')})`
            + (usada.cavaDeslocada > 0 ? ` — a cava foi deslocada ${usada.cavaDeslocada} mm e faz a emenda` : '')
            + (usada.frisosAuto > 0 ? ' — frisos automáticos unem as chapas' : '')
    });
    d.valido = d.conferencias.every((c: any) => c.ok);
    return d;
}
